import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OpportunityFormDialog from "@/components/dialogs/OpportunityFormDialog";
import AiOpportunityFinderDialog from "@/components/dialogs/AiOpportunityFinderDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2, Sparkles } from "lucide-react";

const STATUS_OPTIONS = ["To Apply", "In Progress", "Submitted", "Result Pending", "Selected", "Rejected"];

export default function Opportunities() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [opportunities, setOpportunities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [addOpen, setAddOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = () => api.get("/opportunities").then((r) => setOpportunities(r.data)).catch(() => {});

  useEffect(() => {
    load();
    if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [isAdmin]);

  const employeesMap = Object.fromEntries(employees.map((e) => [e.id, e.name]));

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/opportunities/${id}`, { status });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteOpportunity = async (id) => {
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success("Opportunity removed");
      load();
    } catch (e) {
      toast.error("Could not remove opportunity");
    }
  };

  const filtered = opportunities.filter((o) => statusFilter === "All" || o.status === statusFilter);

  return (
    <DashboardLayout title="Opportunities">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="text-sm text-[#4B5563] max-w-md">Workshops, grants, competitions and collaboration forms sourced from LinkedIn, Instagram and elsewhere — allotted for the team to work on.</p>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="opportunity-status-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button data-testid="ai-find-opportunities-button" onClick={() => setAiOpen(true)} variant="outline" className="border-[#76C121] text-[#00823B] hover:bg-[#F7FEE7]">
              <Sparkles className="h-4 w-4 mr-1.5" /> Find with AI
            </Button>
          )}
          {isAdmin && (
            <Button data-testid="add-opportunity-button" onClick={() => setAddOpen(true)} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Add Opportunity
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="opportunities-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-5 py-3 font-medium">Title</th>
              <th className="px-5 py-3 font-medium">Source</th>
              <th className="px-5 py-3 font-medium">Category</th>
              {isAdmin && <th className="px-5 py-3 font-medium">Assignee</th>}
              <th className="px-5 py-3 font-medium">Deadline</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-[#4B5563]">No opportunities found.</td></tr>
            )}
            {filtered.map((o) => (
              <tr key={o.id} data-testid={`opportunity-row-${o.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                <td className="px-5 py-3 font-medium text-[#1C1C1E] max-w-[220px] truncate">
                  {o.title}
                  {o.external_link && (
                    <a href={o.external_link} target="_blank" rel="noreferrer" data-testid={`opportunity-link-${o.id}`} className="ml-1.5 text-[#00823B] inline-block align-middle">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </td>
                <td className="px-5 py-3 text-[#4B5563]">{o.source}</td>
                <td className="px-5 py-3 text-[#4B5563]">{o.category}</td>
                {isAdmin && <td className="px-5 py-3 text-[#4B5563]">{employeesMap[o.assignee_id] || "Unassigned"}</td>}
                <td className="px-5 py-3 text-[#4B5563]">{o.deadline || "—"}</td>
                <td className="px-5 py-3">
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger data-testid={`opportunity-status-select-${o.id}`} className="h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3 text-right">
                  {isAdmin && (
                    <button onClick={() => deleteOpportunity(o.id)} data-testid={`opportunity-delete-${o.id}`} className="text-[#4B5563] hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <OpportunityFormDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <AiOpportunityFinderDialog open={aiOpen} onOpenChange={setAiOpen} onAdded={load} />
    </DashboardLayout>
  );
}
